/*
 * Admin Home tab: template selection, group selection, confirm, success.
 */

import React from "react";
import { FaTimes, FaCheckCircle } from "react-icons/fa";
import i18n from "../../../i18n/i18n.js";
import { replaceLogoPlaceholders, extractLogoPlaceholders } from "../../utils/templateUtils.js";

export default function AdminHomeTab({
  getTextDirection,
  flowStep,
  setFlowStep,
  templates,
  loadingTemplates,
  selectedTemplate,
  setSelectedTemplate,
  setPlaceholderValues,
  setEditedHtmlContent,
  handleTemplateSelect,
  handleEditTemplate,
  editedHtmlContent,
  logoUrls,
  setLogoUrls,
  setChangesSaved,
  previewRef,
  handleSaveFromPreview,
  handleContinueToGroup,
  loadingGroups,
  groups,
  selectedGroups,
  handleGroupToggle,
  manualEmails,
  setManualEmails,
  handleContinueFromGroup,
  parseManualEmails,
  handleSendMessage,
  sendingMessage,
  successMessage,
  handleResetFlow,
  editingLogoPlaceholder,
  setEditingLogoPlaceholder,
}) {
  const dir = getTextDirection ? getTextDirection() : "ltr";
  const rtl = dir === "rtl";

  return (
    <div className="flex-1 flex flex-col gap-4">
      {/* Step 1: Template Selection */}
      {flowStep === "template" && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-900">{i18n.t("home.sendAwarenessMessage")}</h2>
          <h3 className="text-base font-medium text-gray-700">{i18n.t("home.selectTemplate")}</h3>
          <div>
            <div dir="ltr" className={`flex items-center justify-between mb-2 ${rtl ? "flex-row-reverse" : ""}`}>
              <label className="block text-sm font-medium text-gray-700">{i18n.t("home.chooseTemplate")}</label>
              {selectedTemplate && (
                <button
                  onClick={() => handleEditTemplate(selectedTemplate)}
                  className={`flex items-center gap-1 text-xs text-[#0C75D1] hover:text-[#46A2F2] hover:underline ${rtl ? "flex-row-reverse" : ""}`}
                  title={i18n.t("home.customize")}
                />
              )}
            </div>
            {loadingTemplates ? (
              <div className="text-center py-4 text-gray-600">{i18n.t("home.loadingTemplates")}</div>
            ) : (
              <>
                <select
                  value={selectedTemplate?.id ? String(selectedTemplate.id) : ""}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    if (!selectedId) {
                      setSelectedTemplate(null);
                      setPlaceholderValues({});
                      setEditedHtmlContent(null);
                      return;
                    }
                    const template = templates.find((t) => String(t.id) === selectedId || String(t._id) === selectedId);
                    if (template) handleTemplateSelect(template);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{i18n.t("home.selectTemplatePlaceholder")}</option>
                  {templates.map((template) => (
                    <option key={template.id || template._id} value={String(template.id || template._id)}>
                      {template.name || `Template ${template.id || template._id}`}{" "}
                      {template.isCustomized ? i18n.t("home.customized") : ""}
                    </option>
                  ))}
                </select>
                {templates.length === 0 && (
                  <div className="text-sm text-gray-500 mt-2">
                    <p>{i18n.t("home.noTemplatesFound")}</p>
                  </div>
                )}
              </>
            )}
          </div>

          {selectedTemplate && (
            <>
              <div className="mt-4 p-4 bg-gray-50 rounded-md border border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">{i18n.t("home.preview")}</h4>
                <div className="text-xs text-gray-500 mb-3 space-y-1">
                  <p>• {i18n.t("home.clickTextToEdit")}</p>
                  <p>• {i18n.t("home.clickLogoToEdit")}</p>
                  <p>• {i18n.t("home.addLogoUrlsBelow")}</p>
                </div>
                <div className="space-y-2 text-sm mb-3">
                  <div className="p-2 bg-yellow-50 border font-bold border-yellow-200 rounded-md text-xs text-yellow-900">
                    {i18n.t("home.clickTemplateToEditHint")}
                  </div>
                  <div className="flex items-center gap-2 group">
                    <span className="font-medium">{i18n.t("home.subject")}</span>
                    <div dir="ltr"
                      contentEditable
                      suppressContentEditableWarning
                      onFocus={(e) => {
                        const baseSubject = replaceLogoPlaceholders(
                          editedHtmlContent?.subject || selectedTemplate.subject,
                          logoUrls
                        );
                        e.target.textContent = baseSubject;
                        e.target.style.backgroundColor = "#f0f9ff";
                        e.target.style.border = "1px dashed #0C75D1";
                        e.target.style.padding = "4px 8px";
                        e.target.style.borderRadius = "4px";
                      }}
                      onBlur={(e) => {
                        e.target.style.backgroundColor = "transparent";
                        e.target.style.border = "none";
                        e.target.style.padding = "0";
                        const newSubject = e.target.textContent || e.target.innerText;
                        const currentSubject = editedHtmlContent?.subject || selectedTemplate.subject;
                        if (newSubject !== currentSubject) {
                          setEditedHtmlContent((prev) => ({ ...prev, subject: newSubject }));
                          setChangesSaved(false);
                        }
                      }}
                      className="flex-1 px-2 py-1 border border-transparent hover:border-blue-300 rounded cursor-text focus:outline-none"
                      style={{ minHeight: "24px", outline: "none" }}
                    >
                      {replaceLogoPlaceholders(
                        editedHtmlContent?.subject || selectedTemplate.subject,
                        logoUrls
                      )}
                    </div>
                  </div>
                </div>
                <div className="relative" dir="ltr">
                  <div
                    ref={previewRef}
                    className="border border-gray-300 rounded-md bg-white editable-preview"
                    style={{ maxHeight: "500px", overflowY: "auto", overflowX: "auto", width: "100%" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      dangerouslySetInnerHTML={{
                        __html: replaceLogoPlaceholders(
                          editedHtmlContent?.htmlBody || selectedTemplate.htmlBody,
                          logoUrls
                        ),
                      }}
                      style={{ minWidth: "100%", width: "max-content" }}
                    />
                  </div>
                </div>
                {editedHtmlContent && (
                  <div className="mt-2 text-xs text-green-600">
                    {rtl ? "✓ " : ""}{i18n.t("home.templateCustomized")}{rtl ? "" : " ✓"}
                  </div>
                )}
              </div>

              <div className="mt-4 p-4 bg-blue-50 rounded-md border border-blue-200">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">{i18n.t("home.logoUrls")}</h4>
                <div className="space-y-3">
                  {(() => {
                    const logoPlaceholders = [
                      ...extractLogoPlaceholders(selectedTemplate.subject),
                      ...extractLogoPlaceholders(selectedTemplate.htmlBody),
                    ];
                    const uniquePlaceholders = [...new Set(logoPlaceholders)];
                    const logosToShow = uniquePlaceholders.length > 0 ? uniquePlaceholders : ["LOGO_URL_1"];
                    return logosToShow.map((logoPlaceholder) => (
                      <div key={logoPlaceholder}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          <code className="bg-gray-100 px-2 py-1 rounded text-sm">{"{{" + logoPlaceholder + "}}"}</code>
                        </label>
                        <input
                          type="url"
                          value={logoUrls[logoPlaceholder] || ""}
                          onChange={(e) => {
                            setLogoUrls((prev) => ({ ...prev, [logoPlaceholder]: e.target.value }));
                            setChangesSaved(false);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="https://example.com/logo.png"
                        />
                        {logoUrls[logoPlaceholder] && (
                          <div className="mt-2 p-2 bg-white rounded border border-gray-200">
                            <p className="text-xs text-gray-600 mb-1">{i18n.t("home.logoPreview")}</p>
                            <img
                              src={logoUrls[logoPlaceholder]}
                              alt="Logo preview"
                              className="max-h-20 border border-gray-300 rounded"
                              onError={(e) => {
                                e.target.style.display = "none";
                                const errorMsg = e.target.nextElementSibling;
                                if (errorMsg) errorMsg.style.display = "block";
                              }}
                            />
                            <p className="text-xs text-red-600 mt-1" style={{ display: "none" }}>
                              {i18n.t("home.failedToLoadImage")}
                            </p>
                          </div>
                        )}
                      </div>
                    ));
                  })()}
                </div>
              </div>

              {editingLogoPlaceholder && (
                <div
                  className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
                  onClick={(e) => {
                    if (e.target === e.currentTarget) setEditingLogoPlaceholder(null);
                  }}
                >
                  <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
                    <div className={`flex items-center justify-between mb-4 ${rtl ? "flex-row-reverse" : ""}`}>
                      <h3 className="text-lg font-semibold text-gray-900">{i18n.t("home.updateLogoUrl")}</h3>
                      <button onClick={() => setEditingLogoPlaceholder(null)} className="text-gray-400 hover:text-gray-600">
                        <FaTimes className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {i18n.t("home.logoUrlFor")}{" "}
                          <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                            {"{{" + editingLogoPlaceholder + "}}"}
                          </code>
                        </label>
                        <input
                          type="url"
                          value={logoUrls[editingLogoPlaceholder] || ""}
                          onChange={(e) =>
                            setLogoUrls((prev) => ({ ...prev, [editingLogoPlaceholder]: e.target.value }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="https://example.com/logo.png"
                          autoFocus
                        />
                        <p className="text-xs text-gray-500 mt-1">{i18n.t("home.enterFullLogoUrl")}</p>
                        {logoUrls[editingLogoPlaceholder] && (
                          <div className="mt-3 p-3 bg-gray-50 rounded border border-gray-200">
                            <p className="text-xs text-gray-600 mb-2">{i18n.t("home.logoPreview")}</p>
                            <img
                              src={logoUrls[editingLogoPlaceholder]}
                              alt="Logo preview"
                              className="max-h-32 border border-gray-300 rounded"
                              onError={(e) => {
                                e.target.style.display = "none";
                                if (e.target.nextSibling) e.target.nextSibling.style.display = "block";
                              }}
                            />
                            <p className="text-xs text-red-600 mt-2" style={{ display: "none" }}>
                              {i18n.t("home.failedToLoadImage")}
                            </p>
                          </div>
                        )}
                      </div>
                      <div className={`flex gap-2 ${rtl ? "flex-row-reverse justify-start" : "justify-end"}`}>
                        <button
                          onClick={() => setEditingLogoPlaceholder(null)}
                          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                        >
                          {i18n.t("common.cancel")}
                        </button>
                        <button
                          onClick={() => {
                            if (previewRef?.current && logoUrls[editingLogoPlaceholder]) {
                              const logo = previewRef.current.querySelector(
                                `img[data-logo-placeholder="${editingLogoPlaceholder}"]`
                              );
                              if (logo) {
                                logo.src = logoUrls[editingLogoPlaceholder];
                                logo.style.display = "block";
                              }
                            }
                            setEditingLogoPlaceholder(null);
                            setChangesSaved(false);
                          }}
                          className="px-4 py-2 bg-[#46A2F2] text-white rounded-md hover:bg-[#0C75D1]"
                        >
                          {i18n.t("home.saveLogo")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-4">
                <button
                  onClick={handleContinueToGroup}
                  disabled={loadingTemplates}
                  className="w-full bg-[#46A2F2] text-white px-4 py-2 rounded-md hover:bg-[#0C75D1] focus:outline-none focus:ring-2 focus:ring-[#337ab7] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={i18n.t("home.autoSaveAndContinue")}
                >
                  {loadingTemplates ? i18n.t("common.saving") : i18n.t("home.saveAndContinue")}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Step 2: Group Selection */}
      {flowStep === "group" && (
        <div className="space-y-4">
          <div className={`flex items-center justify-between ${rtl ? "flex-row-reverse" : ""}`} dir="ltr">
            <h3 className="text-lg font-semibold text-gray-900">{i18n.t("home.selectGroup")}</h3>
            <button onClick={() => setFlowStep("template")} className="text-sm text-[#0C75D1] hover:underline">
              {i18n.t("home.back")}
            </button>
          </div>
          {selectedTemplate && (
            <div className="p-3 bg-blue-50 rounded-md border border-blue-200">
              <div className="text-sm">
                <span className="font-medium">{i18n.t("home.template")}</span> {selectedTemplate.name}
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {i18n.t("home.chooseGroup")} <span className="text-xs text-gray-500">{i18n.t("home.selectMultiple")}</span>
            </label>
            {loadingGroups ? (
              <div className="text-center py-4 text-gray-600">{i18n.t("home.loadingGroups")}</div>
            ) : (
              <div className="border border-gray-300 rounded-md max-h-60 overflow-y-auto">
                {groups.length === 0 ? (
                  <p className="text-sm text-gray-500 p-3">{i18n.t("home.noGroupsFound")}</p>
                ) : (
                  groups.map((group) => {
                    const isSelected = selectedGroups.some((g) => g.id === group.id);
                    return (
                      <label
                        key={group.id}
                        className={`flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50 border-b border-gray-200 last:border-b-0 ${isSelected ? "bg-blue-50" : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleGroupToggle(group)}
                          className="mx-3 h-4 w-4 accent-[#0C75D1] text-[#0C75D1] focus:ring-[#0C75D1] border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-900">{group.displayName}</span>
                      </label>
                    );
                  })
                )}
              </div>
            )}
            {selectedGroups.length > 0 && (
              <div className="mt-2 p-2 bg-blue-50 rounded-md border border-blue-200">
                <p className="text-xs font-medium text-blue-900 mb-1">
                  {i18n.t("home.selectedGroups")} ({selectedGroups.length}):
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedGroups.map((group) => (
                    <span
                      key={group.id}
                      className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {group.displayName}
                      <button
                        type="button"
                        onClick={() => handleGroupToggle(group)}
                        className="ml-1 text-blue-600 hover:text-blue-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {i18n.t("home.additionalEmailAddresses")}
            </label>
            <textarea
              value={manualEmails}
              onChange={(e) => setManualEmails(e.target.value)}
              placeholder={`${i18n.t("home.enterEmailAddresses")}\n${i18n.t("home.emailAddressesExample")}`}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={4}
            />
            <p className="text-xs text-gray-500 mt-1">{i18n.t("home.separateEmailsHint")}</p>
          </div>
          <button
            onClick={handleContinueFromGroup}
            className="w-full bg-[#46A2F2] text-white px-4 py-2 rounded-md hover:bg-[#0C75D1] focus:outline-none focus:ring-2 focus:ring-[#337ab7] focus:ring-offset-2"
          >
            {i18n.t("home.continue")}
          </button>
        </div>
      )}

      {/* Step 3: Confirmation */}
      {flowStep === "confirm" && (
        <div className="space-y-4">
          <div className={`flex items-center justify-between ${rtl ? "flex-row-reverse" : ""}`}>
            <h3 className="text-lg font-semibold text-gray-900">{i18n.t("home.confirmSend")}</h3>
            <button onClick={() => setFlowStep("group")} className="text-sm text-[#0C75D1] hover:underline">
              {i18n.t("home.back")}
            </button>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
            <div>
              <span className="text-sm font-medium text-gray-700">{i18n.t("home.template")}</span>
              <div className="mt-1 text-sm text-gray-900">{selectedTemplate?.name}</div>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">{i18n.t("home.group")}</span>
              <div className="mt-1 text-sm text-gray-900">
                {selectedGroups.length > 0
                  ? selectedGroups.map((g) => g.displayName).join(", ")
                  : i18n.t("home.noGroupsSelected")}
              </div>
            </div>
            {manualEmails.trim() && (
              <div>
                <span className="text-sm font-medium text-gray-700">
                  {selectedGroups.length > 0 ? i18n.t("home.ccRecipients") : i18n.t("home.toRecipients")}
                </span>
                <div className="mt-1 text-sm text-gray-900">{parseManualEmails(manualEmails).join(", ")}</div>
              </div>
            )}
            <div className="border-t border-gray-200 pt-4">
              <span className="text-sm font-medium text-gray-700 mb-3 block">{i18n.t("home.emailPreview")}</span>
              <div className="mt-2 border border-gray-300 rounded-md overflow-hidden bg-white">
                <div className="p-2 bg-gray-50 border-b border-gray-200">
                  <div className="text-xs text-gray-600">
                    <strong>{i18n.t("home.subject")}</strong> {selectedTemplate?.subject}
                  </div>
                </div>
                <div className="overflow-auto" style={{ maxHeight: "400px" }} dir="ltr">
                  <div
                    dangerouslySetInnerHTML={{ __html: selectedTemplate?.htmlBody }}
                    style={{
                      transform: "scale(0.7)",
                      transformOrigin: "top left",
                      width: "142.857%",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={handleSendMessage}
            disabled={sendingMessage}
            className="w-full bg-[#46A2F2] text-white px-4 py-2 rounded-md hover:bg-[#0C75D1] focus:outline-none focus:ring-2 focus:ring-[#337ab7] focus:ring-offset-2 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
          >
            {sendingMessage ? i18n.t("home.sending") : i18n.t("home.sendMessage")}
          </button>
        </div>
      )}

      {/* Step 4: Success */}
      {flowStep === "success" && (
        <div className="flex flex-col items-center justify-center flex-1 space-y-4">
          <FaCheckCircle className="w-16 h-16 text-green-500" />
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {i18n.t("home.messageSentSuccessfully")}
            </h3>
            {successMessage && <p className="text-sm text-gray-600 mb-4">{successMessage}</p>}
          </div>
          <button
            onClick={handleResetFlow}
            className="bg-[#46A2F2] text-white px-6 py-2 rounded-md hover:bg-[#0C75D1] focus:outline-none focus:ring-2 focus:ring-[#337ab7] focus:ring-offset-2"
          >
            {i18n.t("home.sendAnotherMessage")}
          </button>
        </div>
      )}
    </div>
  );
}
